import { TransactionType } from "../../../views/Orders/enums";

const DEFAULT_GARAAZ_SUPPLIER_ID = "64ae87c051c0be36170dc718";
export const DEFAULT_MGP_DISTRIBUTOR_ID = "62bbfb934de9282bc818d3a1";

/**
 * `mgp_distributor` is stored as a JSON array string in Mission Control, e.g.
 * ["62bbfb934de9282bc818d3a1","665458aa491fda51733085de"]
 * (or already as an array from the config API).
 */
export function getMgpDistributorIds(
  mgpDistributorConfig?: unknown,
  fallbackId: string = DEFAULT_MGP_DISTRIBUTOR_ID,
): string[] {
  if (Array.isArray(mgpDistributorConfig)) {
    const ids = mgpDistributorConfig
      .map((id) => String(id).trim().toLowerCase())
      .filter(Boolean);
    return ids.length > 0
      ? ids
      : fallbackId
        ? [fallbackId.toLowerCase()]
        : [];
  }

  if (typeof mgpDistributorConfig === "string" && mgpDistributorConfig.trim()) {
    try {
      const parsed = JSON.parse(mgpDistributorConfig);
      if (Array.isArray(parsed)) {
        const ids = parsed
          .map((id) => String(id).trim().toLowerCase())
          .filter(Boolean);
        if (ids.length > 0) return ids;
      }
    } catch {
      // not JSON — treat as a single supplier id
      return [mgpDistributorConfig.trim().toLowerCase()];
    }
  }

  return fallbackId ? [fallbackId.toLowerCase()] : [];
}

function isGaraazSupplier(
  supplierId: string,
  garaazSupplierIdFromConfig?: string,
): boolean {
  return (
    !!supplierId &&
    supplierId === (garaazSupplierIdFromConfig || DEFAULT_GARAAZ_SUPPLIER_ID)
  );
}

export function getParentSupplierId(
  supplierId: string | undefined,
  orderData: any,
): string {
  if (!supplierId || !orderData) return "";
  const allocated = orderData.allocatedTo?.find(
    (item: any) => String(item.supplierId) === String(supplierId),
  );
  if (allocated?.parentSupplierId) {
    return String(allocated.parentSupplierId);
  }
  const association = orderData.customer?.supplierAssociations?.find(
    (assoc: any) => String(assoc.supplierId) === String(supplierId),
  );
  if (association?.supplier?.parentSupplierId) {
    return String(association.supplier.parentSupplierId);
  }
  if (association?.parentSupplierId) {
    return String(association.parentSupplierId);
  }
  return "";
}

export function isMgpDistributorOrOutlet(
  supplierId: string | undefined,
  parentSupplierId: string,
  mgpDistributorConfig?: unknown,
): boolean {
  if (!supplierId) return false;
  const allowedIds = getMgpDistributorIds(mgpDistributorConfig);

  const cleanSupplierId = String(supplierId).trim().toLowerCase();
  const cleanParentId = String(parentSupplierId).trim().toLowerCase();

  return (
    allowedIds.includes(cleanSupplierId) ||
    (cleanParentId.length > 0 && allowedIds.includes(cleanParentId))
  );
}

export function checkCustomerHasMgpAssociation(
  orderData: any,
  mgpDistributorConfig?: unknown,
  allSuppliers?: any[],
): boolean {
  const associations = orderData?.customer?.supplierAssociations;
  if (!associations || !Array.isArray(associations)) {
    return false;
  }

  const mgpIds = getMgpDistributorIds(mgpDistributorConfig);

  return associations.some((assoc: any) => {
    const assocParentId = assoc.parentSupplierId
      ? String(assoc.parentSupplierId).trim().toLowerCase()
      : "";
    if (assocParentId && mgpIds.includes(assocParentId)) {
      return true;
    }

    const assocSupplierId = assoc.supplierId?.$oid
      ? String(assoc.supplierId.$oid).trim().toLowerCase()
      : assoc.supplierId?._id
        ? String(assoc.supplierId._id).trim().toLowerCase()
        : typeof assoc.supplierId === "object"
          ? ""
          : String(assoc.supplierId).trim().toLowerCase();

    if (assocSupplierId && mgpIds.includes(assocSupplierId)) {
      return true;
    }

    const assocSupplierParentId = assoc.supplier?.parentSupplierId
      ? String(assoc.supplier.parentSupplierId).trim().toLowerCase()
      : "";
    if (assocSupplierParentId && mgpIds.includes(assocSupplierParentId)) {
      return true;
    }

    if (assocSupplierId && allSuppliers && Array.isArray(allSuppliers)) {
      const found = allSuppliers.find(
        (s: any) => String(s._id) === assocSupplierId,
      );
      const foundParentId = found?.parentSupplierId
        ? String(found.parentSupplierId).trim().toLowerCase()
        : "";
      if (foundParentId && mgpIds.includes(foundParentId)) {
        return true;
      }
    }

    return false;
  });
}

export function resolveDefaultEstimateSplitTransactionType(params: {
  supplierId?: string | null;
  orderData?: any;
  isSingleGaraazInvoiceRequired?: boolean;
  garaazSupplierIdFromConfig?: string;
  mgpDistributorConfig?: unknown;
  parentSupplierId?: string;
  allSuppliers?: any[];
}): TransactionType | "" {
  const {
    supplierId,
    orderData,
    isSingleGaraazInvoiceRequired,
    garaazSupplierIdFromConfig,
    mgpDistributorConfig,
    parentSupplierId: parentSupplierIdProp,
    allSuppliers,
  } = params;
  if (!supplierId) return "";
  if (
    isGaraazSupplier(supplierId, garaazSupplierIdFromConfig) ||
    isSingleGaraazInvoiceRequired
  ) {
    return TransactionType.TRADING;
  }

  const parentSupplierId =
    parentSupplierIdProp || getParentSupplierId(supplierId, orderData);
  const hasMgpAssociation = checkCustomerHasMgpAssociation(
    orderData,
    mgpDistributorConfig,
    allSuppliers,
  );
  const isMgpSupplier = isMgpDistributorOrOutlet(
    supplierId,
    parentSupplierId,
    mgpDistributorConfig,
  );

  if (hasMgpAssociation && isMgpSupplier) {
    return TransactionType.FACILITATION;
  }
  return TransactionType.TRADING;
}

export function getTradingDefaultsFromOrder(orderData: any): {
  discount: number;
  credit: number;
} {
  const discountFromOrder =
    orderData?.discount != null && orderData.discount !== ""
      ? Number(orderData.discount)
      : 0;
  const creditFromOrder =
    orderData?.creditNotePercentage != null &&
    orderData.creditNotePercentage !== ""
      ? Number(orderData.creditNotePercentage)
      : 0;
  return {
    discount: Number.isFinite(discountFromOrder) ? discountFromOrder : 0,
    credit: Number.isFinite(creditFromOrder) ? creditFromOrder : 0,
  };
}
